import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { API_BASE, ApiError, bindSession } from '../api/client';
import {
  isMfaChallenge,
  type PlatformLoginResult,
  type PlatformMe,
  type PlatformTokens,
} from '../api/m19';
import { roleCan, type Capability } from './capabilities';

/**
 * Platform session: the access token lives in memory only; the refresh token in localStorage
 * (`sm.admin.refresh`) so a reload keeps the admin signed in. Refresh tokens rotate on every use.
 */
export const REFRESH_KEY = 'sm.admin.refresh';

function readRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeRefresh(value: string | null) {
  try {
    if (value) localStorage.setItem(REFRESH_KEY, value);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* storage unavailable: session lasts for this tab only */
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': crypto.randomUUID(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await ApiError.from(res);
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

export interface AuthContextValue {
  status: AuthStatus;
  me: PlatformMe | null;
  /** True when the admin holds every listed capability. */
  can: (...capabilities: Capability[]) => boolean;
  /** MFA is required for this admin but not set up yet: only the setup flow is usable. */
  mfaSetupRequired: boolean;
  /**
   * Step 1 of sign-in. Resolves `null` when signed in, or the MFA challenge token when a code is
   * needed next (`verifyMfa`).
   */
  login: (email: string, password: string) => Promise<{ mfaToken: string } | null>;
  /** Step 2 of sign-in: a 6-digit code or a recovery code (`xxxx-xxxx`). */
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  reloadMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const access = useRef<string | null>(null);
  const refreshToken = useRef<string | null>(readRefresh());
  const [status, setStatus] = useState<AuthStatus>(() => (readRefresh() ? 'loading' : 'signedOut'));
  const [me, setMe] = useState<PlatformMe | null>(null);
  const [forcedMfa, setForcedMfa] = useState(false);

  const accept = useCallback((tokens: PlatformTokens) => {
    access.current = tokens.accessToken;
    refreshToken.current = tokens.refreshToken;
    writeRefresh(tokens.refreshToken);
  }, []);

  const clear = useCallback(() => {
    access.current = null;
    refreshToken.current = null;
    writeRefresh(null);
    setMe(null);
    setForcedMfa(false);
    setStatus('signedOut');
    queryClient.clear();
  }, [queryClient]);

  // A second refresh with an already-rotated token is reuse and ends the session, so concurrent
  // callers (StrictMode's double effects, parallel 401s) share one request.
  const inflight = useRef<Promise<string | null> | null>(null);
  const refresh = useCallback((): Promise<string | null> => {
    const current = refreshToken.current;
    if (!current) return Promise.resolve(null);
    inflight.current ??= post<PlatformTokens>('/platform/auth/refresh', { refreshToken: current })
      .then((tokens) => {
        accept(tokens);
        return tokens.accessToken;
      })
      .catch(() => null)
      .finally(() => {
        inflight.current = null;
      });
    return inflight.current;
  }, [accept]);

  const loadMe = useCallback(async () => {
    const res = await fetch(`${API_BASE}/platform/me`, {
      headers: { Authorization: `Bearer ${access.current ?? ''}` },
    });
    if (!res.ok) throw await ApiError.from(res);
    setMe((await res.json()) as PlatformMe);
    setStatus('signedIn');
  }, []);

  useEffect(() => {
    bindSession({
      accessToken: () => access.current,
      refresh,
      onSessionExpired: clear,
      onMfaSetupRequired: () => setForcedMfa(true),
    });
  }, [refresh, clear]);

  // Resume a stored session once on start.
  useEffect(() => {
    if (!refreshToken.current) return;
    void (async () => {
      const token = await refresh();
      if (!token) {
        clear();
        return;
      }
      await loadMe().catch(clear);
    })();
  }, [refresh, loadMe, clear]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      can: (...caps) => caps.every((c) => roleCan(me?.role, c)),
      mfaSetupRequired: Boolean(me && !me.mfaEnabled && (me.mfaRequired || forcedMfa)),
      login: async (email, password) => {
        const result = await post<PlatformLoginResult>('/platform/auth/login', {
          email,
          password,
        });
        if (isMfaChallenge(result)) return { mfaToken: result.mfaToken };
        accept(result);
        await loadMe();
        return null;
      },
      verifyMfa: async (mfaToken, code) => {
        const tokens = await post<PlatformTokens>('/platform/auth/mfa/verify', { mfaToken, code });
        accept(tokens);
        await loadMe();
      },
      logout: async () => {
        const current = refreshToken.current;
        if (current) {
          await post(
            '/platform/auth/logout',
            { refreshToken: current },
            access.current ?? undefined,
          ).catch(() => undefined);
        }
        clear();
      },
      reloadMe: async () => {
        await loadMe();
        setForcedMfa(false);
      },
    }),
    [status, me, forcedMfa, accept, loadMe, clear],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
